import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
} from 'react';
import ActionPopup from '../../ReusableComponents/ActionPopup';
import { useTranslation } from 'react-i18next';
import Select from '../../ReusableComponents/Select';
import ReportSelection from '../OrderReport/ReportSelection';
import Input from '../../ReusableComponents/Input';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from '../../Utils/utils';
import { onReportOrdered, orderReport } from '../../slices/creditRiskSlice';
import Button from '../../ReusableComponents/Button';
import CorporateReportOptions from '../OrderReport/CorporateReportOptions';
import AdditionalOptions from '../OrderReport/AdditionalOptions';
import ReportInformation from './ReportInformation';

const CreditRiskOrderReport = forwardRef(
  (
    {
      onOrderReport,
      isOrderReport,
      clientId,
      setIsOrderReport,
      isClientAddressFetched,
      clientAddress,
      onCancel,
      onEditClient,
      isClientDetailsFetching,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    useImperativeHandle(ref, () => ({
      onReportValidation,
      onCancelOrderReport,
    }));

    const { userCompanies } = useSelector((state) => state.auth);
    const { isEN } = useSelector((state) => state.common);

    const { isReportOrdering, isReportOrdered } = useSelector(
      (state) => state.creditRisk
    );

    const [referenceNumber, setReferenceNumber] = useState('');
    const [reportErrors, setReportErrors] = useState({});
    const [primaryReports, setPrimaryReports] = useState([]);
    const [company, setCompany] = useState({
      companyId: '',
      error: '',
    });
    const [reportType, setReportType] = useState(`complete_search`);
    const [isCreditLimit, setIsCreditLimit] = useState(false);
    const [address, setAddress] = useState({
      isMissing: false,
      missingFields: [],
    });

    useEffect(() => {
      if (isClientAddressFetched) {
        const fields = [];
        if (isEmpty(clientAddress?.address?.civicNumber)) {
          fields?.push(`civic_number`);
        }
        if (isEmpty(clientAddress?.address?.street)) {
          fields?.push(`street`);
        }
        if (isEmpty(clientAddress?.address?.city)) {
          fields?.push(`city`);
        }
        if (isEmpty(clientAddress?.address?.province)) {
          fields?.push(`province`);
        }
        if (isEmpty(clientAddress?.address?.postalCode)) {
          fields?.push(`postal_code`);
        }
        setAddress((prev) => ({
          ...prev,
          isMissing: fields?.length > 0,
          missingFields: fields,
        }));
      }
    }, [clientAddress, isClientAddressFetched]);

    const onSelection = (optionValue) => {
      setCompany((prev) => ({
        ...prev,
        companyId: optionValue,
        error: '',
      }));
    };

    const onReportValidation = () => {
      if (isEmpty(company?.companyId)) {
        setCompany((prev) => ({
          ...prev,
          error: `${t(`select_account_to_bill`)}`,
        }));
      }
      if (primaryReports?.length === 0) {
        setReportErrors((errors) => ({
          ...errors,
          report: `${t(`report_step_one_error`)}`,
        }));
      }

      if (!isEmpty(company?.companyId) && primaryReports?.length > 0) {
        const payload = {
          clientId: clientId,
          companyId: company?.companyId,
          reports: primaryReports,
          referenceNumber,
          credit_recommended: isCreditLimit,
          incorporate_options: reportType === `name_verification`,
        };
        dispatch(orderReport(payload));
      }
    };

    const onCancelOrderReport = () => {
      setIsOrderReport(false);
      setReportErrors({});
      setPrimaryReports([]);
      setReferenceNumber('');
      onCancel(false);
      setCompany((prev) => ({
        ...prev,
        companyId: '',
        error: '',
      }));
      isReportOrdered && dispatch(onReportOrdered());
    };

    const goToMyReports = () => {
      window.open(`https://portail.alliancecredit.ca/credit-reports`);
    };

    return (
      <ActionPopup
        isVisible={isOrderReport}
        from={`credit_risk_order_report`}
        headerText={`${t(`order_report`)}`}
        buttonText1={`${isReportOrdered ? t(`close`) : t(`cancel`)}`}
        buttonText2={`${t(`order_report`)}`}
        onSubmit={onOrderReport}
        isActionsLeft={true}
        onCancel={onCancelOrderReport}
        isLoading={isReportOrdering}
        isSingleButton={isReportOrdered}
      >
        <div className='credit_risk_order_report_wrapper'>
          {isReportOrdered ? (
            <div className='report_ordered_success_wrapper'>
              <svg
                width='209'
                height='240'
                viewBox='0 0 209 240'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <ellipse
                  cx='35.5561'
                  cy='121.311'
                  rx='25.9955'
                  ry='25.9953'
                  fill='#FAFAFA'
                />
                <ellipse
                  cx='5.22961'
                  cy='100.982'
                  rx='4.99914'
                  ry='4.99909'
                  fill='#FAFAFA'
                />
                <path
                  d='M204.024 211.228L76.2458 239.689C73.113 240.356 70.0468 238.423 69.3803 235.357L27.8541 49.1908C27.1876 46.058 29.1206 42.9919 32.1867 42.3253L160.031 13.8639C163.164 13.1973 166.23 15.1303 166.897 18.1964L208.356 204.296C209.09 207.429 207.157 210.495 204.024 211.228Z'
                  fill='#E0E0E0'
                />
                <path
                  d='M204.018 211.228C204.018 211.228 204.151 211.161 204.418 211.095C204.684 211.028 205.084 210.828 205.618 210.561C206.084 210.295 206.684 209.828 207.217 209.162C207.751 208.495 208.217 207.562 208.417 206.495C208.484 205.962 208.484 205.362 208.417 204.696C208.284 204.096 208.15 203.429 208.017 202.763C207.684 201.43 207.351 199.897 207.017 198.297C206.284 195.098 205.484 191.365 204.551 187.166C200.818 170.369 195.353 146.107 188.687 116.112C185.354 101.115 181.688 84.6511 177.756 67.0543C175.756 58.2559 173.756 49.1243 171.623 39.726C170.557 35.0602 169.49 30.261 168.424 25.4619C167.891 23.0623 167.357 20.5961 166.758 18.1966C166.491 16.9968 165.758 15.9303 164.758 15.1305C163.758 14.3973 162.558 13.9307 161.292 13.9307C160.692 13.9307 160.092 14.064 159.426 14.1973C158.826 14.3306 158.159 14.4639 157.559 14.5972C156.293 14.8638 155.093 15.1305 153.826 15.4637C151.294 15.997 148.761 16.5969 146.228 17.1301C141.162 18.2632 136.03 19.3963 130.83 20.5961C120.432 22.929 109.834 25.2619 99.0359 27.6615C77.5063 32.394 55.1768 37.3264 32.2474 42.4588C30.8477 42.7921 29.5812 43.6586 28.7814 44.7917C27.9815 45.9915 27.6482 47.4579 27.9148 48.8576C28.5147 51.7238 29.1813 54.5233 29.7812 57.3894C31.0476 63.055 32.3141 68.654 33.5139 74.253C35.9801 85.3843 38.4464 96.3823 40.8459 107.18C45.6451 128.776 50.311 149.573 54.7102 169.436C59.1095 189.299 63.3087 208.095 67.2414 225.759C67.708 227.958 68.2412 230.158 68.7078 232.291C68.9744 233.357 69.1744 234.49 69.441 235.49C69.7076 236.557 70.3075 237.49 71.1074 238.223C71.9073 238.956 72.9071 239.423 73.9069 239.623C74.4402 239.689 74.9734 239.689 75.5066 239.689C76.0399 239.623 76.5731 239.489 77.0397 239.356C79.106 238.89 81.1723 238.423 83.2386 237.956C91.4372 236.157 99.3025 234.357 106.768 232.691C136.763 226.025 161.025 220.626 177.822 216.894C186.221 215.027 192.753 213.561 197.152 212.628C199.352 212.161 201.018 211.761 202.151 211.495C203.485 211.295 204.018 211.228 204.018 211.228C204.018 211.228 203.418 211.361 202.285 211.628C201.152 211.895 199.485 212.294 197.286 212.761C192.886 213.761 186.354 215.227 177.956 217.094C161.159 220.826 136.896 226.292 106.901 232.957C99.3692 234.624 91.5705 236.357 83.3719 238.223C81.3056 238.69 79.2393 239.156 77.173 239.623C76.6398 239.756 76.1065 239.889 75.5733 239.956C75.0401 240.023 74.5068 240.023 73.9069 239.889C72.8404 239.689 71.7739 239.223 70.9741 238.423C70.1742 237.69 69.5077 236.69 69.241 235.624C68.9744 234.557 68.7745 233.491 68.5078 232.357C68.0413 230.158 67.508 228.025 67.0414 225.825C63.1754 208.162 58.9761 189.299 54.5103 169.502C50.111 149.639 45.4451 128.843 40.646 107.247C38.2464 96.4489 35.7801 85.451 33.3139 74.3196C32.0475 68.7207 30.8477 63.1217 29.5812 57.4561C28.9147 54.5899 28.3148 51.7904 27.6482 48.9243C27.3816 47.4579 27.7149 45.8582 28.5814 44.6584C29.3812 43.392 30.7144 42.5255 32.1808 42.1922C55.0435 37.0598 77.4396 32.1274 98.9693 27.3282C109.767 24.9287 120.366 22.5958 130.764 20.2629C135.963 19.1297 141.095 17.93 146.161 16.8635C148.694 16.3302 151.227 15.7304 153.76 15.1971C155.026 14.9305 156.293 14.6639 157.493 14.3306C158.092 14.1973 158.759 14.064 159.359 13.9307C159.959 13.7974 160.625 13.6641 161.292 13.6641C162.625 13.6641 163.891 14.1306 164.891 14.9305C165.891 15.7304 166.624 16.8635 166.958 18.1299C167.491 20.5961 168.024 22.9957 168.624 25.3953C169.69 30.1944 170.757 34.9935 171.823 39.726C173.89 49.1243 175.956 58.2559 177.889 67.0543C181.822 84.7178 185.488 101.115 188.82 116.112C195.486 146.107 200.885 170.369 204.618 187.166C205.551 191.365 206.351 195.098 207.084 198.297C207.417 199.897 207.751 201.43 208.084 202.763C208.217 203.429 208.417 204.096 208.484 204.696C208.55 205.296 208.617 205.962 208.484 206.495C208.284 207.629 207.817 208.562 207.284 209.228C206.751 209.895 206.151 210.361 205.618 210.628C205.084 210.895 204.684 211.028 204.418 211.161C204.151 211.161 204.018 211.228 204.018 211.228Z'
                  fill='#263238'
                />
                <g opacity='0.3'>
                  <g opacity='0.3'>
                    <g opacity='0.3'>
                      <path
                        opacity='0.3'
                        d='M82.7072 233.091L199.154 206.496L195.488 183.633L188.889 170.835L77.4414 214.627L82.7072 233.091Z'
                        fill='black'
                      />
                    </g>
                  </g>
                </g>
                <g opacity='0.3'>
                  <g opacity='0.3'>
                    <g opacity='0.3'>
                      <path
                        opacity='0.3'
                        d='M131.297 20.6631L135.696 27.9284L136.563 31.7944L133.563 32.4609L131.297 20.6631Z'
                        fill='black'
                      />
                    </g>
                  </g>
                </g>
                <path
                  d='M34.5078 50.2569L74.9009 231.557L201.546 203.362L194.347 170.901L161.153 22.062L34.5078 50.2569Z'
                  fill='white'
                />
                <path
                  d='M34.5078 50.2569C35.441 50.0569 84.1659 39.1256 161.153 21.9287H161.286V22.062C172.551 72.4528 186.548 135.308 201.746 203.362V203.496H201.612C156.353 213.56 113.361 223.159 74.9675 231.69H74.8342V231.557C50.9716 124.51 34.7078 51.39 34.5078 50.2569C34.7744 51.39 51.105 124.51 75.0342 231.557L74.9009 231.49C113.294 222.959 156.287 213.36 201.546 203.229L201.412 203.362C186.282 135.308 172.284 72.4528 161.019 22.062L161.153 22.1287C84.1659 39.2589 35.3743 50.0569 34.5078 50.2569Z'
                  fill='#263238'
                />
                <path
                  d='M134.297 33.9269L70.1744 48.191L66.9749 33.727C66.4417 31.4607 67.9081 29.1945 70.1744 28.7279L126.031 16.2635C128.298 15.7302 130.564 17.1966 131.097 19.4629L134.297 33.9269Z'
                  fill='#263238'
                />
                <path
                  d='M104.7 13.6641L88.3691 17.3301L93.3683 30.8609L105.699 28.1281L104.7 13.6641Z'
                  fill='#263238'
                />
                <path
                  d='M126.035 16.2649C126.035 16.3315 113.57 19.1977 98.1727 22.597C82.7753 26.0631 70.2441 28.7293 70.2441 28.6626C70.2441 28.5959 82.7087 25.7298 98.1727 22.3304C113.503 18.931 126.035 16.1982 126.035 16.2649Z'
                  fill='#455A64'
                />
                <path
                  d='M70.9707 44.9258C70.904 44.9258 70.2375 42.3262 69.5042 39.0602C68.771 35.7941 68.2378 33.1946 68.3045 33.1279C68.3711 33.1279 69.0377 35.7275 69.7709 38.9935C70.5041 42.2596 71.0373 44.8591 70.9707 44.9258Z'
                  fill='#455A64'
                />
                <path
                  d='M131.228 29.9948C131.162 29.9948 130.562 27.6619 129.895 24.6624C129.229 21.7296 128.762 19.3301 128.829 19.3301C128.895 19.3301 129.495 21.663 130.162 24.6624C130.828 27.5952 131.295 29.9948 131.228 29.9948Z'
                  fill='#455A64'
                />
                <path
                  d='M128.697 33.194C128.697 33.2607 115.899 36.1935 100.035 39.7262C84.1711 43.2589 71.3733 46.0583 71.3066 45.9917C71.3066 45.925 84.1044 42.9922 99.9684 39.4595C115.832 35.8602 128.697 33.0607 128.697 33.194Z'
                  fill='#455A64'
                />
                <path
                  d='M94.9689 85.9187C94.9689 85.9854 86.3703 87.985 75.6388 90.3846C64.974 92.7841 56.3088 94.6505 56.2422 94.5838C56.2422 94.5171 64.8407 92.5175 75.5722 90.118C86.237 87.7184 94.9022 85.8521 94.9689 85.9187Z'
                  fill='#263238'
                />
                <path
                  d='M159.426 78.6525C159.426 78.7192 136.83 83.8516 108.968 90.0504C81.1061 96.2493 58.4434 101.248 58.4434 101.182C58.4434 101.115 81.0395 95.9827 108.901 89.7838C136.763 83.5183 159.426 78.5859 159.426 78.6525Z'
                  fill='#263238'
                />
                <path
                  d='M160.828 84.8508C160.828 84.9174 138.232 90.0498 110.37 96.2487C82.5085 102.448 59.8457 107.447 59.8457 107.38C59.8457 107.313 82.4418 102.181 110.304 95.9821C138.166 89.7832 160.762 84.7841 160.828 84.8508Z'
                  fill='#263238'
                />
                <path
                  d='M162.156 91.1164C162.156 91.183 139.56 96.3154 111.698 102.514C83.8366 108.713 61.1738 113.712 61.1738 113.646C61.1738 113.579 83.7699 108.447 111.632 102.248C139.56 95.9822 162.156 91.0497 162.156 91.1164Z'
                  fill='#263238'
                />
                <path
                  d='M163.559 97.3151C163.559 97.3818 140.963 102.514 113.101 108.713C85.2389 114.912 62.5762 119.911 62.5762 119.844C62.5762 119.778 85.1723 114.645 113.034 108.446C140.963 102.248 163.559 97.2485 163.559 97.3151Z'
                  fill='#263238'
                />
                <path
                  d='M164.957 103.581C164.957 103.647 142.361 108.78 114.499 114.979C86.6374 121.178 63.9746 126.177 63.9746 126.11C63.9746 126.043 86.5707 120.911 114.433 114.712C142.294 108.447 164.957 103.514 164.957 103.581Z'
                  fill='#263238'
                />
                <path
                  d='M83.5697 147.44C83.5697 147.44 83.5698 147.373 83.5031 147.24C83.4364 147.107 83.4364 146.907 83.3698 146.64C83.2365 146.107 83.0365 145.374 82.8365 144.44C82.37 142.507 81.7701 139.775 81.0369 136.575L81.1702 136.642C77.9707 137.375 74.238 138.175 70.2387 139.108C70.2387 139.175 70.4387 138.775 70.372 138.975V139.041V139.108L70.4387 139.308L70.5053 139.708C70.572 139.975 70.6386 140.175 70.6386 140.441C70.772 140.908 70.8386 141.441 70.9719 141.908C71.1719 142.841 71.3719 143.841 71.5718 144.707C71.9717 146.573 72.3717 148.306 72.705 149.839L72.5716 149.773C75.8377 149.106 78.5706 148.506 80.5036 148.106C81.4368 147.906 82.17 147.773 82.7699 147.64C83.0365 147.573 83.2365 147.573 83.3698 147.507C83.5031 147.44 83.5697 147.44 83.5697 147.44C83.5697 147.44 83.5031 147.44 83.3698 147.507C83.2365 147.573 83.0365 147.573 82.7699 147.64C82.2366 147.773 81.5034 147.973 80.5703 148.173C78.6373 148.64 75.9044 149.24 72.6383 150.039H72.505V149.906C72.1717 148.306 71.7718 146.573 71.3718 144.774C71.1719 143.841 70.9719 142.907 70.7053 141.974C70.572 141.508 70.5053 140.974 70.372 140.508C70.3054 140.241 70.2387 140.041 70.2387 139.775L70.1721 139.375L70.1054 139.175V139.108V139.041C70.0387 139.175 70.2387 138.841 70.2387 138.841C74.238 137.975 77.9707 137.108 81.1702 136.442H81.3035V136.575C82.0367 139.841 82.5699 142.574 83.0365 144.574C83.2365 145.507 83.3698 146.24 83.5031 146.773C83.5698 147.04 83.5697 147.173 83.6364 147.373C83.5697 147.373 83.5697 147.44 83.5697 147.44Z'
                  fill='#263238'
                />
                <path
                  d='M164.79 120.505L88.5371 137.496L89.9579 143.872L166.211 126.881L164.79 120.505Z'
                  fill='#E0E0E0'
                />
                <path
                  d='M87.7729 166.37C87.7729 166.37 87.7729 166.303 87.7062 166.17C87.6396 166.036 87.6396 165.836 87.5729 165.57C87.4396 165.036 87.2396 164.303 87.0397 163.37C86.5731 161.437 85.9732 158.704 85.24 155.505L85.3733 155.572C82.1738 156.305 78.4411 157.105 74.4418 158.038C74.4418 158.104 74.6418 157.704 74.5751 157.904V157.971V158.038L74.6418 158.238L74.7085 158.638C74.7751 158.904 74.8418 159.104 74.8418 159.371C74.9751 159.837 75.0417 160.371 75.175 160.837C75.375 161.77 75.575 162.77 75.7749 163.637C76.1749 165.503 76.5748 167.236 76.9081 168.769L76.7748 168.702C80.0409 168.036 82.7737 167.436 84.7067 167.036C85.6399 166.836 86.3731 166.703 86.973 166.57C87.2396 166.503 87.4396 166.503 87.5729 166.436C87.7062 166.37 87.7729 166.37 87.7729 166.37C87.7729 166.37 87.7062 166.37 87.5729 166.436C87.4396 166.503 87.2396 166.503 86.973 166.57C86.4398 166.703 85.7066 166.903 84.7734 167.103C82.8404 167.569 80.1075 168.169 76.8414 168.969H76.7081V168.836C76.3748 167.236 75.9749 165.503 75.575 163.703C75.375 162.77 75.175 161.837 74.9084 160.904C74.7751 160.437 74.7085 159.904 74.5751 159.438C74.5085 159.171 74.4418 158.971 74.4418 158.704L74.3752 158.304L74.3085 158.104V158.038V157.971C74.2419 158.104 74.4418 157.771 74.4418 157.771C78.4411 156.905 82.1738 156.038 85.3733 155.372H85.5066V155.505C86.2398 158.771 86.773 161.504 87.2396 163.503C87.4396 164.437 87.5729 165.17 87.7062 165.703C87.7729 165.97 87.7729 166.103 87.8395 166.303C87.7729 166.303 87.7729 166.37 87.7729 166.37Z'
                  fill='#263238'
                />
                <path
                  d='M169.044 139.43L92.791 156.421L94.2118 162.797L170.465 145.806L169.044 139.43Z'
                  fill='#E0E0E0'
                />
                <path
                  d='M92.0385 185.367C92.0385 185.367 92.0385 185.3 91.9718 185.167C91.9052 185.033 91.9052 184.833 91.8385 184.567C91.7052 184.034 91.5053 183.3 91.3053 182.367C90.8387 180.434 90.2388 177.701 89.5056 174.502L89.6389 174.569C86.4395 175.302 82.7068 176.102 78.7075 177.035C78.7075 177.101 78.9074 176.702 78.8408 176.902V176.968V177.035L78.9074 177.235L78.9741 177.635C79.0407 177.901 79.1074 178.101 79.1074 178.368C79.2407 178.835 79.3074 179.368 79.4407 179.834C79.6406 180.767 79.8406 181.767 80.0406 182.634C80.4405 184.5 80.8404 186.233 81.1737 187.766L81.0404 187.7C84.3065 187.033 87.0394 186.433 88.9724 186.033C89.9055 185.833 90.6387 185.7 91.2386 185.567C91.5053 185.5 91.7052 185.5 91.8385 185.433C91.9052 185.367 92.0385 185.367 92.0385 185.367C92.0385 185.367 91.9718 185.367 91.8385 185.433C91.7052 185.5 91.5053 185.5 91.2386 185.567C90.7054 185.7 89.9722 185.9 89.039 186.1C87.106 186.566 84.3731 187.166 81.107 187.966H80.9737V187.833C80.6405 186.233 80.2405 184.5 79.8406 182.7C79.6406 181.767 79.4407 180.834 79.174 179.901C79.0407 179.434 78.9741 178.901 78.8408 178.435C78.7741 178.168 78.7075 177.968 78.7075 177.701L78.6408 177.301L78.5742 177.101V177.035V176.968C78.5075 177.101 78.7075 176.768 78.7075 176.768C82.7068 175.902 86.4395 175.035 89.6389 174.369H89.7722V174.502C90.5054 177.768 91.0387 180.501 91.5053 182.5C91.7052 183.434 91.8385 184.167 91.9718 184.7C92.0385 184.967 92.0385 185.1 92.1051 185.3C92.0385 185.3 92.0385 185.367 92.0385 185.367Z'
                  fill='#263238'
                />
                <path
                  d='M173.251 158.433L96.998 175.424L98.4188 181.8L174.672 164.809L173.251 158.433Z'
                  fill='#E0E0E0'
                />
                <path
                  d='M73.0488 143.507L74.182 141.707L77.648 143.573L81.9806 136.975L83.7137 138.174L78.048 146.506L73.0488 143.507Z'
                  fill='#21598A'
                />
                <path
                  d='M77.1699 161.837L78.3031 160.038L81.7025 161.904L86.1017 155.305L87.7681 156.505L82.1024 164.837L77.1699 161.837Z'
                  fill='#21598A'
                />
                <path
                  d='M81.3047 180.636L82.5045 178.836L85.9039 180.702L90.2365 174.104L91.9695 175.303L86.3038 183.635L81.3047 180.636Z'
                  fill='#21598A'
                />
                <path
                  d='M116.235 204.228C116.235 204.295 109.836 205.828 101.837 207.561C93.9053 209.361 87.4397 210.694 87.373 210.627C87.373 210.561 93.7719 209.028 101.771 207.295C109.769 205.495 116.235 204.162 116.235 204.228Z'
                  fill='#263238'
                />
                <path
                  d='M130.631 200.765C130.631 200.831 128.298 201.431 125.366 202.098C122.433 202.764 120.033 203.231 120.033 203.164C120.033 203.098 122.366 202.498 125.299 201.831C128.165 201.165 130.565 200.698 130.631 200.765Z'
                  fill='#263238'
                />
                <path
                  d='M178.157 185.832C178.157 185.899 172.358 187.232 165.226 188.832C158.094 190.431 152.229 191.631 152.229 191.565C152.229 191.498 158.028 190.165 165.16 188.565C172.292 186.965 178.157 185.766 178.157 185.832Z'
                  fill='#263238'
                />
                <path
                  d='M171.958 203.363C171.958 203.363 172.025 203.163 172.092 202.829C172.158 202.496 172.158 201.963 172.025 201.296C171.825 200.63 171.492 199.897 171.092 199.03C170.692 198.164 170.159 197.097 170.159 195.831C170.159 194.564 170.692 193.231 171.825 192.231C172.958 191.231 174.691 190.898 176.224 191.431C177.024 191.698 177.757 192.165 178.357 192.831C178.624 193.164 178.824 193.631 178.89 194.098C178.957 194.564 178.557 195.097 178.091 195.231C177.624 195.431 177.224 195.431 176.758 195.564C176.291 195.631 175.891 195.764 175.424 195.831C174.558 196.031 173.625 196.164 172.692 196.297C170.825 196.564 168.959 196.564 167.226 196.297C166.359 196.231 165.493 196.164 164.826 196.697C164.16 197.164 163.893 197.964 163.36 198.63C163.093 198.963 162.827 199.297 162.427 199.43C162.027 199.63 161.56 199.63 161.227 199.497C160.427 199.23 159.894 198.697 159.294 198.364C158.961 198.164 158.694 198.03 158.361 198.03C158.027 198.03 157.694 198.097 157.494 198.23C156.961 198.563 156.694 199.23 156.361 199.697C155.694 200.763 154.495 201.296 153.562 201.296C152.562 201.363 151.762 200.896 151.229 200.496C150.695 200.03 150.429 199.563 150.295 199.23C150.162 198.897 150.162 198.697 150.162 198.697C150.162 198.697 150.229 198.897 150.362 199.23C150.495 199.563 150.762 200.03 151.295 200.43C151.828 200.83 152.562 201.23 153.562 201.163C154.495 201.096 155.561 200.63 156.228 199.563C156.561 199.097 156.828 198.43 157.428 198.03C157.694 197.83 158.094 197.697 158.494 197.764C158.894 197.83 159.227 197.964 159.494 198.164C160.16 198.497 160.694 199.03 161.427 199.23C161.76 199.363 162.16 199.363 162.493 199.163C162.827 199.03 163.093 198.763 163.293 198.43C163.76 197.764 164.093 196.964 164.76 196.431C165.093 196.164 165.559 196.031 166.026 195.964C166.493 195.897 166.893 195.964 167.359 195.964C169.092 196.231 170.892 196.164 172.758 195.897C173.691 195.764 174.558 195.631 175.491 195.431C175.958 195.364 176.358 195.231 176.824 195.164C177.224 195.031 177.691 195.031 178.091 194.831C178.491 194.698 178.757 194.364 178.691 193.964C178.691 193.564 178.491 193.164 178.224 192.898C177.691 192.298 176.957 191.831 176.224 191.565C174.758 191.098 173.091 191.365 172.025 192.298C170.959 193.231 170.425 194.498 170.359 195.697C170.359 196.897 170.825 197.897 171.225 198.83C171.625 199.697 171.958 200.496 172.092 201.163C172.225 201.83 172.225 202.363 172.092 202.763C172.025 203.163 171.958 203.363 171.958 203.363Z'
                  fill='#263238'
                />
                <path
                  d='M185.756 194.831C185.756 194.831 185.623 194.897 185.423 194.964C185.156 195.031 184.823 195.097 184.423 195.23C183.49 195.43 182.223 195.764 180.69 196.097C177.557 196.83 173.225 197.83 168.426 199.03C163.693 200.23 159.36 201.429 156.228 202.229C154.695 202.629 153.428 202.962 152.562 203.229C152.162 203.362 151.828 203.429 151.562 203.496C151.362 203.562 151.229 203.562 151.229 203.562C151.229 203.562 151.362 203.496 151.562 203.429C151.828 203.362 152.162 203.229 152.562 203.096C153.428 202.829 154.695 202.429 156.228 202.029C159.294 201.096 163.626 199.963 168.359 198.697C173.158 197.497 177.491 196.497 180.623 195.83C182.223 195.497 183.49 195.231 184.356 195.031C184.756 194.964 185.089 194.897 185.356 194.831C185.623 194.831 185.756 194.831 185.756 194.831Z'
                  fill='#263238'
                />
                <path
                  d='M148.365 40.5964L150.698 33.1978C148.898 29.5318 147.965 25.3325 148.365 20.9333C149.498 8.33565 160.696 -0.995984 173.294 0.137142C185.959 1.27027 195.224 12.4682 194.091 25.0659C192.957 37.6636 181.759 46.9953 169.161 45.8621C163.896 45.3955 159.563 43.4626 155.964 40.1298L148.365 40.5964Z'
                  fill='#FAFAFA'
                />
                <path
                  d='M148.369 40.5968C148.369 40.5968 148.569 40.5968 148.902 40.5301C149.236 40.5301 149.702 40.4634 150.369 40.3968C151.635 40.3301 153.568 40.1968 156.034 39.9969C158.234 41.9965 161.5 44.1961 166.033 45.1959C167.166 45.4625 168.299 45.5958 169.565 45.7291C170.165 45.7291 170.832 45.7958 171.432 45.7958C172.032 45.7958 172.698 45.7291 173.365 45.6625C175.964 45.3959 178.697 44.7293 181.297 43.4629C183.896 42.1965 186.429 40.3301 188.496 37.9306C190.562 35.531 192.295 32.6649 193.228 29.3988C194.161 26.1327 194.428 22.6 193.828 19.0674C193.695 18.2008 193.428 17.3343 193.228 16.4678C193.161 16.0012 192.961 15.6013 192.761 15.2014C192.628 14.8015 192.428 14.3349 192.295 13.935C191.495 12.2686 190.628 10.6689 189.495 9.20249C188.962 8.46929 188.296 7.80274 187.696 7.1362C187.429 6.80292 187.029 6.53631 186.696 6.20303C186.363 5.93642 186.029 5.60314 185.696 5.26987L184.63 4.47002L184.096 4.07009L183.496 3.73682L182.363 3.07027C181.963 2.87031 181.563 2.67035 181.163 2.47038C177.964 0.937329 174.431 0.204129 171.032 0.270784C167.632 0.337438 164.366 1.13729 161.567 2.53704C158.701 3.93678 156.301 5.80311 154.368 8.00271C152.435 10.2023 151.035 12.6019 150.102 15.0681C149.969 15.4014 149.902 15.668 149.769 16.0012C149.636 16.3345 149.569 16.6011 149.502 16.9344C149.369 17.5343 149.169 18.1342 149.036 18.7341C148.902 19.9339 148.636 21.1336 148.636 22.2668C148.436 26.8659 149.636 30.5986 150.969 33.2648C150.169 35.6643 149.569 37.464 149.169 38.7304C148.969 39.3303 148.836 39.7969 148.702 40.1302C148.369 40.4634 148.369 40.5968 148.369 40.5968C148.369 40.5968 148.436 40.4634 148.502 40.1302C148.636 39.7969 148.769 39.3303 148.902 38.7304C149.302 37.464 149.902 35.5977 150.635 33.1981V33.2648C149.302 30.5986 148.036 26.7993 148.236 22.2001C148.236 21.067 148.502 19.8672 148.636 18.6674C148.769 18.0675 148.902 17.4676 149.102 16.8678C149.169 16.5345 149.236 16.2679 149.369 15.9346C149.502 15.6013 149.569 15.3347 149.702 15.0014C150.635 12.5352 152.035 10.069 153.968 7.8694C155.901 5.6698 158.367 3.73682 161.234 2.33707C164.1 0.937329 167.432 0.0708204 170.832 0.00416591C174.231 -0.0624886 177.831 0.670711 181.097 2.20376C181.497 2.40373 181.897 2.60369 182.297 2.80365L183.496 3.53685L184.096 3.87013L184.63 4.27005L185.763 5.06991C186.096 5.33653 186.429 5.6698 186.829 6.00307C187.162 6.33634 187.562 6.60296 187.829 6.93623C188.429 7.60278 189.095 8.26932 189.629 9.06918C190.828 10.5356 191.695 12.2019 192.495 13.8683C192.695 14.2682 192.828 14.7348 192.961 15.1347C193.095 15.6013 193.295 16.0012 193.428 16.4678C193.628 17.3343 193.961 18.2008 194.028 19.134C194.628 22.7333 194.428 26.3327 193.428 29.5988C192.495 32.9315 190.762 35.8643 188.629 38.2638C186.496 40.6634 183.963 42.5297 181.297 43.7962C178.631 45.0626 175.898 45.7958 173.231 45.9958C172.565 45.9958 171.898 46.0624 171.298 46.0624C170.632 46.0624 170.032 45.9958 169.432 45.9958C168.166 45.9291 167.033 45.7292 165.899 45.4625C161.367 44.4627 158.034 42.1965 155.901 40.1968H155.968C153.502 40.3301 151.569 40.3968 150.235 40.4634C149.636 40.4634 149.102 40.5301 148.769 40.5301C148.502 40.5968 148.369 40.5968 148.369 40.5968Z'
                  fill='#263238'
                />
                <path
                  d='M158.223 22.2002L162.022 18.9342L168.354 25.4663L182.685 13.5352L185.685 17.3345L167.421 32.1984L158.223 22.2002Z'
                  fill='#21598A'
                />
                <g clipPath='url(#clip0_9961_6390)'>
                  <path
                    d='M109.817 65.3043L107.717 65.773L108.388 63.8423L109.06 61.9118L110.489 63.3736L111.917 64.8358L109.817 65.3043ZM109.342 61.1748L109.521 60.5242L114.929 66.0276L115.636 65.8699L111.947 62.0942L109.208 59.2907L106.963 65.9336L106.293 66.0907L108.75 58.8215L108.259 58.3188L106.525 63.3042L105.272 66.9067L112.29 65.3406L112.763 65.8229L105.05 67.5442L104.791 68.29L110.214 67.0799L114.258 66.1775L109.342 61.1748Z'
                    fill='url(#paint0_radial_9961_6390)'
                  />
                  <path
                    d='M78.5287 70.7322L78.1316 70.3206L77.9507 70.8612L78.5287 70.7322ZM77.0254 72.2349L77.9461 69.4895L79.9571 71.5806L79.4484 71.6942L78.85 71.065L77.802 71.3023L77.5274 72.1228L77.0254 72.2349Z'
                    fill='#21598A'
                  />
                  <path
                    d='M80.959 71.3569L80.4191 68.9375L80.8587 68.8394L81.3089 70.8567L83.2678 70.4195L83.3576 70.8217L80.959 71.3569Z'
                    fill='#21598A'
                  />
                  <path
                    d='M84.3594 70.5981L83.8194 68.1787L84.2591 68.0806L84.7093 70.0979L86.6681 69.6608L86.7578 70.0629L84.3594 70.5981Z'
                    fill='#21598A'
                  />
                  <path
                    d='M87.6614 67.3208L88.2014 69.7403L87.7617 69.8384L87.2218 67.419L87.6614 67.3208Z'
                    fill='#21598A'
                  />
                  <path
                    d='M90.7123 68.0135L90.3152 67.6018L90.1343 68.1425L90.7123 68.0135ZM89.209 69.5161L90.1297 66.7707L92.1406 68.8619L91.6319 68.9754L91.0335 68.3463L89.9856 68.5836L89.7109 69.4041L89.209 69.5161Z'
                    fill='#21598A'
                  />
                  <path
                    d='M93.1426 68.6382L92.6026 66.2188L92.9384 66.1438L95.1092 67.4862L94.7209 65.746L95.1673 65.6464L95.7072 68.0659L95.3646 68.1423L93.1964 66.7956L93.5822 68.5401L93.1426 68.6382Z'
                    fill='#21598A'
                  />
                  <path
                    d='M98.7194 65.6939L98.6854 65.6555C98.6707 65.6374 98.6593 65.6188 98.6449 65.6041L98.6074 65.5665C98.5975 65.5545 98.5849 65.5467 98.5647 65.537C98.3508 65.3931 97.9721 65.4385 97.7022 65.4987C97.0169 65.6517 96.7477 65.9105 96.8857 66.5289C97.0245 67.1506 97.3781 67.2704 98.0634 67.1174C98.3333 67.0572 98.6954 66.9374 98.8287 66.7196C98.842 66.699 98.8501 66.6864 98.8548 66.6747C98.8587 66.6597 98.8628 66.6447 98.8726 66.6246C98.8794 66.6054 98.8811 66.5802 98.8875 66.561L98.9004 66.5049L99.2972 66.7357L99.2913 66.7582C99.2657 66.8386 99.2279 66.9144 99.1779 66.9823C98.9583 67.2689 98.495 67.4398 98.1524 67.5163C97.2316 67.7217 96.6283 67.4768 96.439 66.6286C96.2512 65.7871 96.6925 65.3056 97.6132 65.1001C97.9558 65.0237 98.4478 64.9812 98.7683 65.1473C98.8425 65.1875 98.909 65.2402 98.9655 65.2984L98.9812 65.3199L98.7194 65.6939Z'
                    fill='#21598A'
                  />
                  <path
                    d='M102.025 65.1303L102.114 65.5325L100.619 65.8661L100.764 66.5149L102.723 66.0778L102.812 66.4799L100.414 67.0151L99.8741 64.5957L102.273 64.0605L102.363 64.4659L100.404 64.903L100.529 65.464L102.025 65.1303Z'
                    fill='#21598A'
                  />
                  <path
                    d='M118.657 61.2525L118.623 61.2141C118.608 61.196 118.597 61.1774 118.582 61.1628L118.545 61.1251C118.535 61.1131 118.522 61.1053 118.502 61.0955C118.288 60.9517 117.91 60.9971 117.639 61.0574C116.954 61.2103 116.685 61.4692 116.823 62.0875C116.962 62.7092 117.315 62.829 118.001 62.6761C118.271 62.6158 118.633 62.496 118.766 62.2783C118.78 62.2575 118.787 62.245 118.792 62.2334C118.796 62.2183 118.8 62.2033 118.81 62.1833C118.817 62.164 118.818 62.1388 118.825 62.1196L118.838 62.0635L119.235 62.2944L119.229 62.3169C119.203 62.3972 119.165 62.473 119.115 62.5409C118.896 62.8276 118.432 62.9984 118.09 63.0749C117.169 63.2804 116.566 63.0354 116.377 62.1872C116.189 61.3457 116.63 60.8643 117.551 60.6588C117.893 60.5823 118.385 60.5399 118.706 60.7059C118.78 60.7461 118.846 60.7988 118.903 60.857L118.919 60.8785L118.657 61.2525Z'
                    fill='#21598A'
                  />
                  <path
                    d='M121.582 60.8658C121.876 60.8002 122.131 60.7362 122.068 60.4558L122.05 60.3713C122.016 60.2227 121.922 60.1728 121.807 60.1631C121.744 60.1593 121.676 60.1637 121.607 60.1792C121.533 60.1886 121.461 60.2083 121.392 60.2238L120.343 60.4578L120.488 61.1099L121.582 60.8658ZM121.284 59.8256C121.807 59.7089 122.359 59.6744 122.492 60.2692L122.513 60.3637C122.617 60.8299 122.322 61.0731 121.885 61.2133L123.057 61.97L122.461 62.1028L121.302 61.3506L120.582 61.5113L120.8 62.4736L120.354 62.5732L119.814 60.1538L121.284 59.8256Z'
                    fill='#21598A'
                  />
                  <path
                    d='M125.673 59.8608L125.763 60.2629L124.268 60.5966L124.412 61.2454L126.371 60.8082L126.461 61.2103L124.062 61.7456L123.523 59.3262L125.921 58.7909L126.012 59.1963L124.053 59.6335L124.178 60.1945L125.673 59.8608Z'
                    fill='#21598A'
                  />
                  <path
                    d='M128.579 60.319C128.866 60.2549 129.205 60.1296 129.332 59.8849C129.424 59.7084 129.402 59.4826 129.359 59.29C129.317 59.1006 129.241 58.8906 129.083 58.7697C128.864 58.6022 128.504 58.6329 128.217 58.697L127.455 58.8669L127.817 60.4889L128.579 60.319ZM128.128 58.2984C128.547 58.2049 129.045 58.1896 129.387 58.4538C129.629 58.6409 129.747 58.9268 129.807 59.1971C129.99 60.0149 129.575 60.5154 128.668 60.7178L127.467 60.9858L126.927 58.5664L128.128 58.2984Z'
                    fill='#21598A'
                  />
                  <path
                    d='M130.984 57.6617L131.524 60.0811L131.084 60.1792L130.544 57.7598L130.984 57.6617Z'
                    fill='#21598A'
                  />
                  <path
                    d='M134.04 59.5197L133.597 59.6185L133.15 57.6005L132.081 57.8391L131.991 57.437L134.57 56.8616L134.66 57.2637L133.59 57.5024L134.04 59.5197Z'
                    fill='#21598A'
                  />
                </g>
                <defs>
                  <radialGradient
                    id='paint0_radial_9961_6390'
                    cx='0'
                    cy='0'
                    r='1'
                    gradientUnits='userSpaceOnUse'
                    gradientTransform='translate(109.236 62.6994) rotate(-12.58) scale(5.09898 6.47181)'
                  >
                    <stop stop-color='#30B3C6' />
                    <stop offset='0.109076' stop-color='#31B3C6' />
                    <stop offset='0.115613' stop-color='#34B5C5' />
                    <stop offset='0.210598' stop-color='#23ADC8' />
                    <stop offset='0.81743' stop-color='#21598A' />
                    <stop offset='1' stop-color='#21598A' />
                  </radialGradient>
                  <clipPath id='clip0_9961_6390'>
                    <rect
                      width='59.51'
                      height='8.97629'
                      fill='white'
                      transform='matrix(0.975993 -0.217803 0.217808 0.975992 75.5488 65.6187)'
                    />
                  </clipPath>
                </defs>
              </svg>

              <div className='order_success_text'>{`${t(
                `order_success`
              )}`}</div>
              <div className='order_success_description'>
                {`${t(`order_success_description`)}`}
              </div>
              <Button
                type='primary'
                text={`${t(`goto_my_reports`)}`}
                isShowIcon={true}
                iconPosition={`after`}
                onClickHandler={goToMyReports}
                icon={
                  <svg
                    width='8'
                    height='12'
                    viewBox='0 0 8 12'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M7.17742 5.39436C7.51213 5.72907 7.51213 6.27264 7.17742 6.60734L2.03628 11.7485C1.70157 12.0832 1.158 12.0832 0.823296 11.7485C0.488586 11.4138 0.488586 10.8702 0.823296 10.5355L5.35928 5.99951L0.825975 1.46353C0.491266 1.12882 0.491266 0.585253 0.825975 0.250544C1.16068 -0.0841655 1.70425 -0.0841654 2.03896 0.250544L7.1801 5.39168L7.17742 5.39436Z'
                      fill='white'
                    />
                  </svg>
                }
              />
            </div>
          ) : (
            <>
              <Select
                options={userCompanies?.map((company) => ({
                  name: isEN
                    ? company?.company_name?.en
                    : company?.company_name?.fr,
                  value: company?._id,
                }))}
                text={`${t(`select_account_to_bill`)}`}
                onOptionCallback={() => {}}
                isError={company?.error}
                errorMessage={company?.error}
                onOptionSelection={onSelection}
                onBlur={() => {}}
                isClosed={!isOrderReport}
              />
              <ReportInformation
                clientAddress={clientAddress}
                address={address}
                onEditClient={onEditClient}
                isClientDetailsFetching={isClientDetailsFetching}
              />

              <ReportSelection
                isFromMonitoring={true}
                reportErrors={reportErrors}
                setReportErrors={setReportErrors}
                primaryReports={primaryReports}
                setPrimaryReports={setPrimaryReports}
                isDisabled={address?.isMissing}
              />
              {clientAddress?.region === `Rest of Canada` &&
                primaryReports?.includes(`Incorporate`) && (
                  <CorporateReportOptions
                    reportType={reportType}
                    setReportType={setReportType}
                  />
                )}
              {primaryReports?.includes(`ac_global`) && (
                <AdditionalOptions
                  isCreditLimit={isCreditLimit}
                  setIsCreditLimit={setIsCreditLimit}
                />
              )}
              <Input
                text={`${t(`reference_number`)}`}
                onBlur={() => {}}
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                isHideMessage={true}
              />
            </>
          )}
        </div>
      </ActionPopup>
    );
  }
);

export default CreditRiskOrderReport;
